<template>
  <div class="graph-out bg-white position-relative elevation-1">
    <!-- Loading State -->
    <div
      v-if="loading"
      class="d-flex align-center justify-center"
      style="height: 180px;"
    >
      <v-progress-circular
        indeterminate
        color="#1f4978"
        size="36"
        width="3"
      ></v-progress-circular>
    </div>

    <!-- Row with stats and chart -->
    <v-row
      v-else
      no-gutters
      align="center"
    >
      <v-col
        cols="12"
        md="3"
        class="pr-md-4 mb-4 mb-md-0"
      >
        <p class="text-p-title font-weight-semibold">
          Active and Inactive Customers
        </p>
      </v-col>

      <v-col
        cols="12"
        md="9"
        class="position-relative pl-md-4"
      >
        <!-- Apex Chart canvas container -->
        <div class="chart-area">
          <vue-apex-charts
            type="area"
            height="180"
            :options="chartOptions"
            :series="chartSeries"
          ></vue-apex-charts>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import { mdiDownload } from '@mdi/js'

export default {
	name: 'AccountsChart',
	components: { VueApexCharts: () => import('vue-apexcharts') },
	props: {
		value: {
			type: String,
			default: 'Monthly',
		},
		chartData: {
			type: Object,
			default: () => ({ active: [], inactive: [] }),
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},
	setup(props, { emit }) {
		const internalFilter = ref(props.value)
		const selectedOutlet = ref('All')

		const timeRanges = ['Daily', 'Weekly', 'Monthly', 'Yearly']
		const outlets = ['All', 'Salmiya', 'Hawally', 'Farwaniya']

		watch(() => props.value, newVal => {
			internalFilter.value = newVal
		})

		const changeFilter = filter => {
			internalFilter.value = filter
			emit('input', filter)
		}

		// Chart Series computed reactively
		const chartSeries = computed(() => [
			{
				name: 'Active Customers',
				data: props.chartData?.active || [],
			},
			{
				name: 'Inactive Customers',
				data: props.chartData?.inactive || [],
			},
		])

		const chartCategories = computed(() => {
			if (props.value === 'Daily') {
				const categories = []
				for (let i = 6; i >= 0; i -= 1) {
					const date = new Date()
					date.setDate(date.getDate() - i)
					categories.push(date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }))
				}

				return categories
			}
			if (props.value === 'Weekly') {
				return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
			}
			if (props.value === 'Yearly') {
				return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
			}

			return ['Week 1', 'Week 2', 'Week 3', 'Week 4']
		})

		// ApexCharts options to match pixel-perfect layout and lines
		const chartOptions = computed(() => ({
			grid: {
				show: true,
				borderColor: '#e5e7eb',
				strokeDashArray: 4,
			},
			chart: {
				toolbar: { show: false },
				sparkline: { enabled: false },
				animations: { enabled: true },
			},
			dataLabels: { enabled: false },
			stroke: {
				curve: 'smooth',
				width: 3,
			},
			colors: ['#007AFF', '#627092'],
			fill: {
				type: 'gradient',
				gradient: {
					shadeIntensity: 1,
					opacityFrom: 0.3,
					opacityTo: 0.05,
					stops: [0, 90, 100],
				},
			},
			xaxis: {
				categories: chartCategories.value,
				labels: {
					show: true,
					style: {
						colors: '#6b7280',
						fontSize: '11px',
						fontFamily: 'Inter, sans-serif',
					},
				},
				axisBorder: { show: false },
				axisTicks: { show: false },
			},
			yaxis: {
				min: 0,
				labels: {
					show: true,
					style: {
						colors: '#6b7280',
						fontSize: '11px',
						fontFamily: 'Inter, sans-serif',
					},
					formatter: val => Math.round(val),
				},
			},
			legend: {
				show: true,
				position: 'top',
				horizontalAlign: 'right',
				labels: { colors: '#374151' },
			},
			tooltip: { enabled: true },
		}))

		// Download mock action
		const downloadChart = () => {
			// Mock action emitting success message using main theme notifier
			emit('show-notification', {
				message: 'Accounts chart exported as PNG successfully!',
				type: 'success',
			})
		}

		return {
			internalFilter,
			selectedOutlet,
			timeRanges,
			outlets,
			chartSeries,
			chartOptions,
			changeFilter,
			downloadChart,
			icons: { mdiDownload },
		}
	},
}
</script>

<style lang="scss" scoped>
.graph-out {
	border-radius: 30px;
	padding: 30px 25px;
	height: 100%;
}

.graph-controls {
	position: absolute;
	top: 15px;
	right: 20px;
	display: flex;
	gap: 10px;
	align-items: center;
	background: #f1f5fd;
	padding: 4px 10px;
	border-radius: 50px;
	z-index: 10;
}

.dropdown-pill {
	background: #fff;
	padding: 6px 12px;
	border-radius: 20px;
	font-size: 13px;
	color: #1f4978;
	display: flex;
	align-items: center;
	gap: 5px;
	font-weight: 600;
	cursor: pointer;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	transition: all 0.2s ease;

	&:hover {
		background: #fcfcfc;
	}
}

.dropdown-pill span {
	color: #767676;
	font-weight: 500;
}

.download-icon {
	background: #fff;
	padding: 5px;
	border-radius: 50%;
	color: #1f4978;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	transition: all 0.2s ease;

	&:hover {
		background: #fcfcfc;
		transform: scale(1.05);
	}
}

.blue {
	background: linear-gradient(135deg, #0b2240 0%, #1f4978 50%, var(--v-primary-base) 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	display: inline-block;
}

.text-slate {
	color: #627092;
}

.text-p-title {
	color: #767676;
	font-weight: 500;
	font-size: 14px;
	margin-bottom: 2px;
}

.chart-area {
	position: relative;
	height: 180px;
}
</style>
